/**
 * ImpressionsSession — the DSP-facing "Impressions" screen.
 *
 * A stripped, phone-first relative of InterviewSession. Same elicitation engine
 * (create → answer loop → finish), but none of the coordinator machinery: no
 * per-objective review, no repair, no "feed to extraction," no breadcrumbs. A
 * DSP answers and is done.
 *
 * Phone-first / desktop-adaptive design decisions (each also a candidate
 * principle for the wider mobile re-design):
 *
 *   • ONE UNLOCK GESTURE — a single "Start" tap begins the conversation AND
 *     satisfies the browser's autoplay policy, so the first question can be
 *     spoken aloud. speechSynthesis / Audio.play() are BOTH gesture-gated; a
 *     cold auto-speak on load fails silently on iOS Safari and Chrome. The tap
 *     is what makes "audio on by default" actually work. (verified against
 *     useSpeech: it falls back to browser TTS on autoplay-block, but the very
 *     first utterance still needs a gesture — hence Start.)
 *
 *   • AUDIO ON BY DEFAULT — auto-read starts ON (the interview screen's opt-in
 *     checkbox is inverted here to an audio-OFF toggle). The DSP hears each
 *     question without opting in.
 *
 *   • AUDIO-FIRST ANSWER, REVEAL-TO-TYPE — the 🎙 record button is the primary
 *     action; the text box is hidden behind a small "Type instead" link and
 *     costs no real-estate until chosen. On a phone that keeps the answer UI to
 *     one thumb-sized target instead of a permanently-open textarea.
 *
 *   • EASY TO STOP — an "I'm done" control is reachable at every turn; earlier
 *     answers are always saved (the backend finish handler is idempotent and
 *     keeps whatever was captured).
 *
 *   • PHONE-FIRST, DESKTOP-ADAPTIVE — a single fluid, max-width-centered column;
 *     larger touch targets simply become comfortable click targets on desktop.
 *
 * All styling uses existing tokens (tokens.css) — no new/private tokens, per the
 * re-design's "extend one system, never fork it" rule.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useSpeech } from "../../hooks/useSpeech.js";
import { useInterimTranscript } from "../../hooks/useInterimTranscript.js";
import LoadingSpinner from "../../components/LoadingSpinner.jsx";

const TAP = 44; // minimum touch target (px) — candidate shared token --tap-target-min

// ── Audio capture ─────────────────────────────────────────────────────────────
// Lifted from InterviewSession's AudioRecorder and re-pointed at the engagement
// audio endpoint. Record → upload (multipart) → server transcribes via PE →
// returns { text }. Text is always available as the baseline; this is additive.

function AudioAnswer({ clientId, engagementId, turnIndex, disabled, onTranscribed, onError }) {
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [unsupported, setUnsupported] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const maxTimerRef = useRef(null);
  const interim = useInterimTranscript();

  const MAX_MS = 5 * 60 * 1000; // 5 min cap — trailing silence makes transcribers hallucinate

  useEffect(() => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) setUnsupported(true);
  }, []);

  async function start() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = handleStop;
      mediaRecorderRef.current = rec;
      rec.start();
      setRecording(true);
      interim.reset(); interim.start();
      clearTimeout(maxTimerRef.current);
      maxTimerRef.current = setTimeout(() => {
        if (mediaRecorderRef.current?.state === "recording") stop();
      }, MAX_MS);
    } catch {
      setUnsupported(true); // no mic / no permission → text baseline remains
    }
  }

  function stop() {
    clearTimeout(maxTimerRef.current);
    mediaRecorderRef.current?.stop();
    mediaRecorderRef.current?.stream?.getTracks().forEach((t) => t.stop());
    interim.stop();
    setRecording(false);
  }

  async function handleStop() {
    const blob = new Blob(chunksRef.current, { type: "audio/webm" });
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", blob, "answer.webm");
      // Multipart — bypasses the JSON api client on purpose (same as interview).
      const res = await fetch(
        `/api/clients/${clientId}/engagements/${engagementId}/audio`,
        { method: "POST", body: form }
      );
      const data = await res.json();
      // Backend soft-fails transcription with 200 + empty text + error, so check
      // the text, not just res.ok. Empty text with an error → degrade to typing.
      if (!res.ok || (!data.text && data.error)) {
        onError("We couldn't catch that — please type your answer instead.");
        return;
      }
      if (!data.text) {
        // Genuine silence (no error): nudge, don't fail.
        onError("Didn't hear anything — tap to try again, or type your answer.");
        return;
      }
      onTranscribed(data.text);
    } catch {
      onError("Couldn't reach the server — please type your answer instead.");
    } finally {
      setUploading(false);
      interim.reset();
    }
  }

  if (unsupported) return null; // text box is always the baseline

  return (
    <div style={aa.wrap}>
      {!recording && !uploading && (
        <button type="button" style={aa.recordBtn} onClick={start} disabled={disabled}>
          🎙 Tap to speak
        </button>
      )}
      {recording && (
        <button type="button" style={aa.stopBtn} onClick={stop}>
          ⏹ Stop &amp; send
        </button>
      )}
      {recording && interim.supported && (
        <p style={aa.interim}>
          {interim.interim ? interim.interim : <span style={aa.listening}>Listening…</span>}
        </p>
      )}
      {uploading && <p style={aa.status}>Got it — one moment…</p>}
    </div>
  );
}

// ── The session ───────────────────────────────────────────────────────────────

export default function ImpressionsSession() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // entry_context comes from where the DSP launched: the player end-branch passes
  // ?context=stream_complete|stream_abandoned; the hub link passes persistent.
  const entryContext = params.get("context") || "stream_complete";
  const progressParam = params.get("progress");

  const { speak, cancel, unlock, supported: ttsSupported, available: ttsAvailable } = useSpeech();

  const [phase, setPhase] = useState("intro"); // intro | asking | finishing | done | error
  const [clientName, setClientName] = useState("");
  const [session, setSession] = useState(null);
  const [question, setQuestion] = useState("");
  const [audioOn, setAudioOn] = useState(true); // audio ON by default
  const [showType, setShowType] = useState(false); // reveal-to-type
  const [typed, setTyped] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState(""); // soft in-flow messages (degrade hints)
  const [fatal, setFatal] = useState("");

  // Client name for the intro copy (best-effort; the session works without it).
  useEffect(() => {
    let live = true;
    fetch(`/api/clients/${clientId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (live && d) setClientName(d.display_name || ""); })
      .catch(() => {});
    return () => { live = false; };
  }, [clientId]);

  // Speak each new question when audio is on. Gated on `phase === "asking"` so it
  // only fires after the Start gesture (which unlocked audio). This is why the
  // first question can be spoken at all.
  useEffect(() => {
    if (phase === "asking" && audioOn && ttsSupported && question) speak(question);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question, phase, audioOn]);

  // The one unlock gesture: start the session (fires the first question) AND, by
  // virtue of being a user tap, unlocks audio so speak() will play the good MP3.
  //
  // iPhone Chrome is stricter than Mac Chrome / iPhone Safari about WHEN and on
  // WHICH element audio unlocks. The real speak(question) fires later (after the
  // create-session round-trip + a re-render), by which point the gesture window
  // has closed — and it plays on the hook's persistent audio element. So we call
  // useSpeech.unlock() SYNCHRONOUSLY here, inside the tap, before any await: it
  // primes that exact element with a silent clip, so the real MP3 plays instead
  // of falling back to the browser voice. No-op-safe where already unlocked.
  async function handleStart() {
    unlock(); // MUST run first, synchronously, inside the gesture — before any await.
    setSubmitting(true);
    setNotice("");
    try {
      const body = { purpose: "app_eval", entry_context: entryContext };
      if (progressParam != null) {
        const p = parseFloat(progressParam);
        if (!Number.isNaN(p)) body.progress_at_entry = p;
      }
      const res = await fetch(`/api/clients/${clientId}/engagements`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || data.directive === "error") {
        // A 403 carries the reason (a contributor, Oct 24) — show it, not "try again".
        setNotice(data.respondent_fallback
                  || (res.status === 403 && data.error)
                  || "That didn't start — please try again.");
        setSubmitting(false);
        return;
      }
      setSession(data.session);
      setQuestion(data.asked_question || "");
      setPhase("asking");
    } catch {
      setNotice("Couldn't reach the server — please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function submitAnswer(answerText, answeredVia) {
    const text = (answerText || "").trim();
    if (!text || !session) return;
    setSubmitting(true);
    setNotice("");
    cancel(); // stop any question audio still playing
    try {
      const res = await fetch(
        `/api/clients/${clientId}/engagements/${session.id}/answer`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ answer: text, answered_via: answeredVia }),
        }
      );
      const data = await res.json();
      setTyped("");
      setShowType(false);

      if (!res.ok) {
        setNotice("That didn't go through — please try again, or tap \"I'm done\".");
        setSubmitting(false);
        return;
      }
      if (data.directive === "error") {
        setNotice(data.respondent_fallback || "There was a hiccup — try again, or tap \"I'm done\".");
        setSubmitting(false);
        return;
      }
      if (data.directive === "session_complete") {
        setSession(data.session);
        setPhase("done");
        setSubmitting(false);
        return;
      }
      // ask_question (or advanced_no_question — treat as "keep going": re-submit
      // is not needed; a no-question advance is rare and the next question will
      // arrive on the following answer). Normal path: show the next question.
      setSession(data.session);
      setQuestion(data.asked_question || "");
      setSubmitting(false);
    } catch {
      setNotice("Couldn't reach the server — please try again.");
      setSubmitting(false);
    }
  }

  async function handleDone() {
    if (!session) { navigate(`/clients/${clientId}`); return; }
    setPhase("finishing");
    cancel();
    try {
      await fetch(`/api/clients/${clientId}/engagements/${session.id}/finish`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: "user_done" }),
      });
    } catch {
      /* finish is best-effort; earlier answers are already saved server-side */
    }
    setPhase("done");
  }

  const turnIndex = session?.conversation?.length || 0;

  // ── Render ──────────────────────────────────────────────────────────────────

  if (phase === "finishing") return <LoadingSpinner message="Saving your thoughts…" />;

  if (phase === "done") {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <h1 style={s.thanksTitle}>Thank you</h1>
          <p style={s.thanksBody}>
            Your impressions help us make {clientName ? `meeting ${clientName}` : "this"} better
            for the next person who supports them.
          </p>
          <button type="button" style={s.primaryBtn} onClick={() => navigate(`/clients/${clientId}`)}>
            Done
          </button>
        </div>
      </div>
    );
  }

  if (phase === "intro") {
    const name = clientName || "this person";
    return (
      <div style={s.page}>
        <div style={s.card}>
          <p style={s.eyebrow}>A quick conversation</p>
          <h1 style={s.introTitle}>
            {entryContext === "stream_abandoned"
              ? `Mind sharing a thought about ${name}?`
              : `What were your first impressions of ${name}?`}
          </h1>
          <p style={s.introBody}>
            A few short questions — just talk, we'll listen. It takes a couple of minutes,
            and you can stop whenever you like.
          </p>
          {notice && <p style={s.notice}>{notice}</p>}
          <button type="button" style={s.primaryBtn} onClick={handleStart} disabled={submitting}>
            {submitting ? "Starting…" : "▶ Start"}
          </button>
          <p style={s.introHint}>You'll be able to speak your answers, or type if you prefer.</p>
        </div>
      </div>
    );
  }

  // phase === "asking"
  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.topRow}>
          {/* No toggle when the server cannot speak (Plan H2): the question is
              read on screen and there is no voice to turn on. */}
          {ttsAvailable ? (
            <button
              type="button"
              style={s.audioToggle}
              onClick={() => { setAudioOn((v) => { if (v) cancel(); return !v; }); }}
              aria-pressed={audioOn}
              title={audioOn ? "Turn question audio off" : "Turn question audio on"}
            >
              {audioOn ? "🔊 Audio on" : "🔈 Audio off"}
            </button>
          ) : <span />}
          <button type="button" style={s.doneLink} onClick={handleDone}>
            I'm done
          </button>
        </div>

        <p style={s.question}>{question}</p>

        {notice && <p style={s.notice}>{notice}</p>}

        {/* Audio-first answer. Text box hidden until "Type instead". */}
        {!showType && (
          <>
            <AudioAnswer
              clientId={clientId}
              engagementId={session.id}
              turnIndex={turnIndex}
              disabled={submitting}
              onTranscribed={(t) => submitAnswer(t, "voice")}
              onError={(m) => setNotice(m)}
            />
            <button type="button" style={s.typeLink} onClick={() => setShowType(true)}>
              ⌨ Type instead
            </button>
          </>
        )}

        {showType && (
          <div style={s.typeWrap}>
            <textarea
              style={s.textarea}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="Type your answer…"
              rows={4}
              autoFocus
            />
            <div style={s.typeActions}>
              <button
                type="button"
                style={s.linkBtn}
                onClick={() => { setShowType(false); setTyped(""); }}
              >
                Speak instead
              </button>
              <button
                type="button"
                style={s.primaryBtn}
                onClick={() => submitAnswer(typed, "text")}
                disabled={submitting || !typed.trim()}
              >
                {submitting ? "Sending…" : "Send"}
              </button>
            </div>
          </div>
        )}

        {submitting && !showType && <p style={s.status}>One moment…</p>}
      </div>
    </div>
  );
}

// ── Styles (existing tokens only) ─────────────────────────────────────────────

const s = {
  page: {
    minHeight: "100dvh",
    background: "var(--color-bg-base)",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    padding: "var(--space-6)",
  },
  card: {
    width: "100%",
    maxWidth: 560, // comfortable centered column on desktop; full width on phone
    margin: "0 auto",
    marginTop: "var(--space-8)",
    background: "var(--color-surface)",
    border: "1px solid var(--color-border)",
    borderRadius: "var(--radius-xl)",
    padding: "var(--space-8)",
    boxShadow: "var(--shadow-sm)",
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-5)",
  },
  eyebrow: {
    fontSize: "var(--text-xs)",
    fontWeight: "var(--weight-semibold)",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "var(--color-text-tertiary)",
    margin: 0,
  },
  introTitle: {
    fontSize: "var(--text-2xl)",
    fontWeight: "var(--weight-bold)",
    color: "var(--color-text-primary)",
    lineHeight: "var(--leading-tight)",
    margin: 0,
  },
  introBody: {
    fontSize: "var(--text-base)",
    color: "var(--color-text-secondary)",
    lineHeight: "var(--leading-normal)",
    margin: 0,
  },
  introHint: {
    fontSize: "var(--text-sm)",
    color: "var(--color-text-tertiary)",
    margin: 0,
  },
  topRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "var(--space-3)",
  },
  audioToggle: {
    fontSize: "var(--text-sm)",
    color: "var(--color-text-secondary)",
    background: "var(--color-bg-subtle)",
    border: "1px solid var(--color-border)",
    borderRadius: "var(--radius-md)",
    padding: "var(--space-2) var(--space-3)",
    minHeight: TAP,
    cursor: "pointer",
  },
  doneLink: {
    fontSize: "var(--text-sm)",
    color: "var(--color-text-tertiary)",
    background: "none",
    border: "none",
    cursor: "pointer",
    minHeight: TAP,
    padding: "var(--space-2)",
    textDecoration: "underline",
  },
  question: {
    fontSize: "var(--text-xl)",
    fontWeight: "var(--weight-semibold)",
    color: "var(--color-text-primary)",
    lineHeight: "var(--leading-tight)",
    margin: 0,
  },
  typeLink: {
    fontSize: "var(--text-sm)",
    color: "var(--color-accent)",
    background: "none",
    border: "none",
    cursor: "pointer",
    alignSelf: "flex-start",
    minHeight: TAP,
    padding: "var(--space-2) 0",
  },
  typeWrap: { display: "flex", flexDirection: "column", gap: "var(--space-3)" },
  textarea: {
    width: "100%",
    boxSizing: "border-box",
    fontSize: "var(--text-base)",
    fontFamily: "inherit",
    color: "var(--color-text-primary)",
    background: "var(--color-bg-base)",
    border: "1px solid var(--color-border)",
    borderRadius: "var(--radius-md)",
    padding: "var(--space-3)",
    lineHeight: "var(--leading-normal)",
    resize: "vertical",
  },
  typeActions: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "var(--space-3)" },
  primaryBtn: {
    fontSize: "var(--text-base)",
    fontWeight: "var(--weight-semibold)",
    color: "var(--color-text-on-accent)",
    background: "var(--color-accent)",
    border: "none",
    borderRadius: "var(--radius-md)",
    padding: "var(--space-3) var(--space-6)",
    minHeight: TAP,
    cursor: "pointer",
    alignSelf: "stretch",
  },
  linkBtn: {
    fontSize: "var(--text-sm)",
    color: "var(--color-text-secondary)",
    background: "none",
    border: "none",
    cursor: "pointer",
    minHeight: TAP,
    padding: "var(--space-2)",
  },
  status: { fontSize: "var(--text-sm)", color: "var(--color-text-tertiary)", fontStyle: "italic", margin: 0 },
  notice: {
    fontSize: "var(--text-sm)",
    color: "var(--color-text-secondary)",
    background: "var(--color-bg-subtle)",
    border: "1px solid var(--color-border)",
    borderRadius: "var(--radius-md)",
    padding: "var(--space-3)",
    margin: 0,
  },
  thanksTitle: {
    fontSize: "var(--text-2xl)",
    fontWeight: "var(--weight-bold)",
    color: "var(--color-text-primary)",
    margin: 0,
  },
  thanksBody: {
    fontSize: "var(--text-base)",
    color: "var(--color-text-secondary)",
    lineHeight: "var(--leading-normal)",
    margin: 0,
  },
};

const aa = {
  wrap: { display: "flex", flexDirection: "column", gap: "var(--space-3)", alignItems: "stretch" },
  recordBtn: {
    fontSize: "var(--text-lg)",
    fontWeight: "var(--weight-semibold)",
    color: "var(--color-text-on-accent)",
    background: "var(--color-accent)",
    border: "none",
    borderRadius: "var(--radius-lg)",
    padding: "var(--space-5) var(--space-6)",
    minHeight: 64, // deliberately larger than TAP — this is THE primary action
    cursor: "pointer",
  },
  stopBtn: {
    fontSize: "var(--text-lg)",
    fontWeight: "var(--weight-semibold)",
    color: "#fff",
    background: "var(--color-critical)",
    border: "none",
    borderRadius: "var(--radius-lg)",
    padding: "var(--space-5) var(--space-6)",
    minHeight: 64,
    cursor: "pointer",
  },
  interim: {
    fontSize: "var(--text-base)",
    color: "var(--color-text-primary)",
    background: "var(--color-bg-subtle)",
    border: "1px solid var(--color-border)",
    borderRadius: "var(--radius-md)",
    padding: "var(--space-3)",
    margin: 0,
    minHeight: "1.4em",
    lineHeight: "var(--leading-normal)",
  },
  listening: { color: "var(--color-text-tertiary)", fontStyle: "italic" },
  status: { fontSize: "var(--text-sm)", color: "var(--color-text-tertiary)", fontStyle: "italic", margin: 0 },
};
